<template>
  <div class="rp" @click.self="$emit('close')">
    <div class="rp__dialog" role="dialog" aria-modal="true" :aria-label="`Place the ${product.name} in your room`">
      <header class="rp__header">
        <div>
          <strong>Place the {{ product.name }} in your room</strong>
          <span>Drag it where you want it, set the size and angle — then we make it look real.</span>
        </div>
        <button type="button" class="rp__close" aria-label="Close" @click="$emit('close')">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </header>

      <div ref="stage" class="rp__stage" :style="stageStyle">
        <img ref="photo" :src="photoUrl" crossorigin="anonymous" class="rp__photo" alt="" @load="onPhotoLoad" />
        <div
          v-if="ready"
          class="rp__model"
          :style="{ left: `${pos.x}px`, top: `${pos.y}px`, width: `${size}px`, height: `${size}px` }"
          @pointerdown="startDrag"
        >
          <model-viewer
            ref="viewer"
            :src="product.model_glb_url"
            :camera-orbit="`${yaw}deg 80deg auto`"
            camera-target="auto auto auto"
            field-of-view="30deg"
            shadow-intensity="0"
            exposure="1"
            interaction-prompt="none"
            disable-zoom
            disable-pan
            disable-tap
            style="width: 100%; height: 100%; background: transparent; pointer-events: none;"
          ></model-viewer>
        </div>
        <p v-if="!ready" class="rp__loading">Loading the photo and the 3D model…</p>
      </div>

      <div class="rp__controls">
        <label>Size <input v-model.number="size" type="range" :min="minSize" :max="maxSize" step="2" /></label>
        <label>Angle <input v-model.number="yaw" type="range" min="-180" max="180" step="5" /></label>
        <label class="rp__placement">Where? <input v-model="placement" type="text" maxlength="120" placeholder="optional, e.g. against the left wall" /></label>
      </div>

      <footer class="rp__footer">
        <span class="rp__hint">The picture costs one of your room pictures for today.</span>
        <div class="rp__actions">
          <button type="button" class="rp__btn rp__btn--ghost" @click="$emit('close')">Cancel</button>
          <button type="button" class="rp__btn rp__btn--dark" :disabled="!ready || busy" @click="finish">{{ busy ? 'Preparing…' : 'Make it real' }}</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, onMounted, ref } from 'vue';

defineProps({
  product: { type: Object, required: true },
  photoUrl: { type: String, required: true },
});
const emit = defineEmits(['close', 'done']);

const stage = ref(null);
const photo = ref(null);
const viewer = ref(null);
const ready = ref(false);
const busy = ref(false);
const natural = ref({ w: 0, h: 0 });
const stageSize = ref({ w: 0, h: 0 });
const pos = ref({ x: 0, y: 0 });
const size = ref(200);
const yaw = ref(20);
const placement = ref('');

const minSize = computed(() => Math.max(60, Math.round(stageSize.value.w * 0.15)));
const maxSize = computed(() => Math.max(120, Math.round(stageSize.value.w * 0.9)));
const stageStyle = computed(() => (stageSize.value.h ? { height: `${stageSize.value.h}px` } : {}));

async function onPhotoLoad() {
  natural.value = { w: photo.value.naturalWidth, h: photo.value.naturalHeight };
  const maxW = Math.min(stage.value.clientWidth || 800, 900);
  const maxH = Math.min(window.innerHeight * 0.55, 600);
  const scale = Math.min(maxW / natural.value.w, maxH / natural.value.h);
  stageSize.value = { w: Math.round(natural.value.w * scale), h: Math.round(natural.value.h * scale) };
  size.value = Math.round(stageSize.value.w * 0.45);
  pos.value = { x: Math.round((stageSize.value.w - size.value) / 2), y: Math.round(stageSize.value.h - size.value * 0.95) };
  try {
    await import('@google/model-viewer');
  } catch (e) { /* already loaded */ }
  ready.value = true;
}

let drag = null;
function startDrag(event) {
  drag = { x: event.clientX - pos.value.x, y: event.clientY - pos.value.y };
  event.currentTarget.setPointerCapture?.(event.pointerId);
  window.addEventListener('pointermove', onDrag);
  window.addEventListener('pointerup', stopDrag, { once: true });
}
function onDrag(event) {
  if (!drag) return;
  pos.value = {
    x: Math.max(-size.value / 2, Math.min(stageSize.value.w - size.value / 2, event.clientX - drag.x)),
    y: Math.max(-size.value / 2, Math.min(stageSize.value.h - size.value / 2, event.clientY - drag.y)),
  };
}
function stopDrag() {
  drag = null;
  window.removeEventListener('pointermove', onDrag);
}

// Photo + the model's transparent render → one JPEG at the photo's own resolution (capped).
async function finish() {
  if (!viewer.value) return;
  busy.value = true;
  try {
    const mv = viewer.value;
    const modelPng = await (mv.toBlob ? mv.toBlob({ mimeType: 'image/png', idealAspect: false }) : null);
    const modelImg = await loadImage(modelPng ? URL.createObjectURL(modelPng) : mv.toDataURL('image/png'));

    const cap = 1536;
    const scale = Math.min(1, cap / Math.max(natural.value.w, natural.value.h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(natural.value.w * scale);
    canvas.height = Math.round(natural.value.h * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(photo.value, 0, 0, canvas.width, canvas.height);
    const k = canvas.width / stageSize.value.w;
    ctx.drawImage(modelImg, pos.value.x * k, pos.value.y * k, size.value * k, size.value * k);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    emit('done', { composite: blob, placement: placement.value.trim() });
  } catch (e) {
    emit('done', { error: 'The picture could not be prepared in this browser — we will place the piece from the product photo instead.', placement: placement.value.trim() });
  } finally {
    busy.value = false;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function onKey(e) {
  if (e.key === 'Escape') emit('close');
}
onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => { window.removeEventListener('keydown', onKey); stopDrag(); });
</script>

<style>
.rp { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 12px; background: rgba(0, 0, 0, 0.7); }
.rp__dialog { width: 100%; max-width: 960px; max-height: 96vh; display: flex; flex-direction: column; background: #fff; border-radius: 16px; overflow: hidden; }
.rp__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 14px 18px; border-bottom: 1px solid #e8e9ea; }
.rp__header strong { display: block; font-size: 16px; color: #172430; }
.rp__header span { display: block; margin-top: 2px; font-size: 13px; color: #8a9197; }
.rp__close { flex: 0 0 36px; height: 36px; border-radius: 999px; border: 0; background: #f6f2ec; color: #172430; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.rp__stage { position: relative; margin: 0 auto; max-width: 100%; background: #111; overflow: hidden; touch-action: none; user-select: none; }
.rp__photo { display: block; width: 100%; height: 100%; object-fit: contain; }
.rp__model { position: absolute; cursor: grab; outline: 1.5px dashed rgba(187, 151, 109, 0.9); outline-offset: -1px; border-radius: 8px; }
.rp__model:active { cursor: grabbing; }
.rp__loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; margin: 0; color: #fff; font-size: 14px; }
.rp__controls { display: grid; grid-template-columns: 1fr 1fr 2fr; gap: 14px; padding: 12px 18px; border-top: 1px solid #e8e9ea; font-size: 13px; color: #3c474e; }
.rp__controls label { display: flex; flex-direction: column; gap: 4px; }
.rp__controls input[type="range"] { accent-color: #bb976d; }
.rp__placement input { height: 36px; padding: 0 10px; border: 1px solid #dfe2e4; border-radius: 8px; font-size: 13px; color: #172430; }
.rp__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 18px; border-top: 1px solid #e8e9ea; }
.rp__hint { font-size: 12px; color: #8a9197; }
.rp__actions { display: flex; gap: 8px; }
.rp__btn { height: 38px; padding: 0 16px; border-radius: 999px; font-size: 13px; font-weight: 600; border: 1px solid transparent; cursor: pointer; }
.rp__btn--dark { background: #172430; color: #fff; }
.rp__btn--dark:hover { background: #bb976d; }
.rp__btn--dark:disabled { opacity: 0.5; cursor: wait; }
.rp__btn--ghost { background: #fff; color: #172430; border-color: #dfe2e4; }
@media (max-width: 640px) { .rp__controls { grid-template-columns: 1fr 1fr; } .rp__placement { grid-column: span 2; } .rp__footer { flex-direction: column; align-items: stretch; } .rp__actions { justify-content: flex-end; } }
.dark .rp__dialog { background: #1e2a35; }
.dark .rp__header, .dark .rp__controls, .dark .rp__footer { border-color: #3c474e; }
.dark .rp__header strong, .dark .rp__controls, .dark .rp__btn--ghost { color: #fff; }
.dark .rp__btn--ghost { background: #22313d; border-color: #3c474e; }
.dark .rp__placement input { background: #172430; border-color: #3c474e; color: #fff; }
</style>
